import { createContext, useContext, useState, useEffect } from 'react';
import Router from 'next/router';

// Create an AuthContext for global state access
export const AuthContext = createContext();

/**
 * Custom hook for using authentication
 * @returns {Object} Auth methods and state
 */
export const useAuth = () => useContext(AuthContext);

/**
 * Auth Provider component for wrapping the application
 * @param {Object} props - Component props
 * @returns {JSX.Element} Auth Provider component
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state on component mount
  useEffect(() => {
    // Check if token is in localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse stored user', e);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  /**
   * Login function - authenticates user with email/password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Authentication result
   */
  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Authentication failed');
      }

      // Store token in localStorage for API requests
      localStorage.setItem('token', data.token);
      
      // Store user in localStorage
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Update state
      setUser(data.user);
      setLoading(false);
      
      return data;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  /**
   * Logout function - removes token and user data
   * @returns {Promise<void>}
   */
  const logout = async () => {
    setLoading(true);

    try {
      // Call logout API to clear server-side cookies
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Update state
      setUser(null);
      
      // Redirect to login page
      Router.push('/auth/signin');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get token from localStorage
   * @returns {string|null} JWT token
   */
  const getToken = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('token');
    }
    return null;
  };

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  const isAuthenticated = () => {
    return !!user;
  };

  // Auth context value
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    getToken,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Higher Order Component to protect routes on client side
 * @param {Function} Component - Component to protect
 * @param {Object} options - Protection options
 * @returns {Function} Protected component
 */
export const withAuthProtection = (Component, options = {}) => {
  const { redirectUrl = '/auth/signin' } = options;

  const ProtectedRoute = (props) => {
    const { user, loading, isAuthenticated } = useAuth();
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
      setMounted(true);
    }, []);

    useEffect(() => {
      if (mounted && !loading) {
        // Not authenticated
        if (!isAuthenticated()) {
          Router.replace(`${redirectUrl}?callbackUrl=${encodeURIComponent(window.location.pathname)}`);
          return;
        }
      }
    }, [mounted, loading, isAuthenticated]);

    // Show loading or render component
    if (loading || !mounted || !isAuthenticated()) {
      return <div>Loading...</div>;
    }

    return <Component {...props} user={user} />;
  };

  return ProtectedRoute;
};

/**
 * Fetch wrapper with authentication token
 * @param {string} url - API URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export const authFetch = async (url, options = {}) => {
  // Get token from localStorage
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  
  // Set default headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  // Add Authorization header if token exists
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  // Make the request
  return fetch(url, {
    ...options,
    headers,
  });
}; 